import React, { useEffect, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';

import { css, keyframes, Theme, useTheme } from '@emotion/react';
import { mq } from '@hangar31/rc-layout';
import Icon from '@OnArk/SVGs/Icon';
import posthog from '@OnArk/utils/posthog';
import Cookies from 'universal-cookie';

import { STEP_SEEN_EVENT } from './markStepSeen';
import { COUNTS_CHANGED_EVENT } from './signalCountsChanged';
import StepCheck from './StepCheck';
import { getStepHref, groupLabels, StepGroup, StepKey, steps } from './steps';

const COUNTS_CACHE_KEY = 'onark-onboarding-counts';

interface Counts {
  courses: number;
  users: number;
  automations: number;
  sectionsBeyondDefault: boolean;
  anyPublished: boolean;
  firstCourseId: string | null;
  firstCourseUrl: string | null;
  churchUrl: string | null;
  churchName: string | null;
  churchLogo: string | null;
  hasStripeCustomer: boolean;
  hasPco: boolean;
  ministries: number;
}

// Shape of the raw GraphQL response, matching the selection set in the query
// below. `fetch().json()` is `any`; asserting this contract types every read
// downstream and removes the per-callback `any` annotations. (The ideal fix is
// a codegen'd typed operation, but that needs the schema server — follow-up.)
interface CountsResponse {
  data?: {
    courses?: {
      id: string;
      url: string;
      changed?: { id: string }[] | null;
      sections?: { id: string; automations?: { id: string }[] | null }[] | null;
    }[];
    users?: { id: string }[];
    church?: {
      url?: string | null;
      name?: string | null;
      logo?: string | null;
      customer?: string | null;
      integrations?: { settings?: { source?: string } | null }[] | null;
    } | null;
    ministries?: { id: string }[];
  };
}

// Total automations configured across every section of every course.
function countAutomations(
  courses: NonNullable<CountsResponse['data']>['courses']
): number {
  return (courses ?? []).reduce(
    (sum, c) =>
      sum +
      (c.sections ?? []).reduce(
        (s2, sec) => s2 + (sec.automations?.length ?? 0),
        0
      ),
    0
  );
}

type CourseList = NonNullable<NonNullable<CountsResponse['data']>['courses']>;
type ChurchData = NonNullable<CountsResponse['data']>['church'];

function mapCourseCounts(
  courses: CourseList
): Pick<
  Counts,
  | 'anyPublished'
  | 'automations'
  | 'courses'
  | 'firstCourseId'
  | 'firstCourseUrl'
  | 'sectionsBeyondDefault'
> {
  const { id: firstCourseId = null, url: firstCourseUrl = null } =
    courses[0] ?? {};

  return {
    anyPublished: courses.some(c => (c.changed?.length ?? 1) === 0),
    automations: countAutomations(courses),
    courses: courses.length,
    firstCourseId,
    firstCourseUrl,
    sectionsBeyondDefault: courses.some(c => (c.sections?.length ?? 0) > 2),
  };
}

function mapChurchCounts(
  church: ChurchData
): Pick<
  Counts,
  'churchLogo' | 'churchName' | 'churchUrl' | 'hasPco' | 'hasStripeCustomer'
> {
  const integrations = church?.integrations ?? [];
  const {
    customer,
    logo: churchLogo = null,
    name: churchName = null,
    url: churchUrl = null,
  } = church ?? {};

  return {
    churchLogo,
    churchName,
    churchUrl,
    // PCO is identified by its `settings.source` (see getting-started.tsx). The
    // previous `type === 'PCO'` check was dead code: IntegrationsType is only
    // 'DATASOURCE' | 'LIVE_CHAT', so the widget's PCO step never ticked.
    hasPco: integrations.some(i => i?.settings?.source === 'PCO'),
    hasStripeCustomer: !!customer,
  };
}

// Shape the onboarding widget's completion signals from the raw GraphQL
// response. Pure so its null-handling stays out of fetchCounts' control flow.
function mapCountsResponse(j: CountsResponse): Counts {
  return {
    ...mapCourseCounts(j.data?.courses ?? []),
    ...mapChurchCounts(j.data?.church),
    ministries: (j.data?.ministries ?? []).length,
    users: (j.data?.users ?? []).length,
  };
}

async function fetchCounts(): Promise<Counts | null> {
  const cookies = new Cookies();
  const token = cookies.get<string | undefined>('onark-token');

  if (!token) return null;
  const endpoint = process.env.NEXT_PUBLIC_GRAPHQL_URI!;

  if (!endpoint) return null;
  try {
    const r = await fetch(endpoint, {
      body: JSON.stringify({
        query: `{
          courses: findManyCourse(where:{church:{id:{equals:"CHURCH_ID"}}}){ id url changed { id } sections { id automations { id } } }
          users: findManyUser(where:{churches:{some:{churchId:{equals:"CHURCH_ID"}}}}){ id }
          church: findUniqueChurch(where:{id:"CHURCH_ID"}){ url name logo customer integrations { settings } }
          ministries: findManyMinistry(where:{church:{id:{equals:"CHURCH_ID"}}}){ id }
        }`,
      }),
      headers: { authorization: token, 'Content-Type': 'application/json' },
      method: 'POST',
    });
    const j = (await r.json()) as CountsResponse;

    return mapCountsResponse(j);
  } catch {
    return null;
  }
}

const DISMISS_KEY = 'onark-onboarding-dismissed';
const MANUAL_KEY = 'onark-onboarding-manual-completed';

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

function loadJSON<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    return JSON.parse(
      localStorage.getItem(key) || JSON.stringify(fallback)
    ) as T;
  } catch {
    return fallback;
  }
}

interface WidgetProps {
  // Subset of completion signals that can be derived from data already in
  // the `me` query (which Layout has access to without extra requests).
  // Other steps are checked manually or on the dedicated page.
  signals: Partial<Record<StepKey, boolean>>;
}

const ProgressRing = ({
  completed,
  theme,
  total,
}: {
  completed: number;
  total: number;
  theme: Theme;
}) => {
  const size = 36;
  const stroke = 3;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = total > 0 ? completed / total : 0;

  return (
    <span
      aria-hidden
      css={{
        alignItems: 'center',
        display: 'inline-flex',
        flexShrink: 0,
        height: size,
        justifyContent: 'center',
        position: 'relative',
        width: size,
      }}
    >
      <svg css={{ transform: 'rotate(-90deg)' }} height={size} width={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          fill="none"
          r={r}
          stroke={theme.colors.border}
          strokeWidth={stroke}
        />
        <circle
          css={{ transition: 'stroke-dasharray 0.3s' }}
          cx={size / 2}
          cy={size / 2}
          fill="none"
          r={r}
          stroke={theme.colors.clickable}
          strokeDasharray={`${c * pct} ${c}`}
          strokeLinecap="round"
          strokeWidth={stroke}
        />
      </svg>
      <span
        css={{
          color: theme.colors.clickable,
          fontSize: '1rem',
          fontWeight: 600,
          left: 0,
          letterSpacing: '-0.5px',
          position: 'absolute',
          right: 0,
          textAlign: 'center',
          top: '50%',
          transform: 'translateY(-50%)',
        }}
      >
        {completed}/{total}
      </span>
    </span>
  );
};

const Widget: React.FC<WidgetProps> = ({ signals }) => {
  const theme = useTheme();
  const router = useRouter();
  // Start false to avoid an SSR-vs-client mismatch dance: the widget renders
  // null SSR anyway because router.query is empty there, and on the client
  // the useEffect below upgrades to the real localStorage value before any
  // interactive paint. Starting true caused the post-effect re-render to
  // be skipped in some Next 14 / React 19 combinations and the widget
  // stayed hidden forever even with no dismiss flag set.
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [manual, setManual] = useState<Record<string, boolean>>({});
  const [counts, setCounts] = useState<Counts | null>(null);

  // Shared refresh — pulls counts via raw fetch and caches them. Called on
  // initial mount AND after every SPA route change so creating a class on
  // /admin/classes then navigating to its editor flips the course step's
  // checkmark and populates firstCourseId for the next-step links.
  const refreshCounts = React.useCallback(() => {
    void fetchCounts().then(c => {
      if (c) {
        setCounts(c);
        sessionStorage.setItem(COUNTS_CACHE_KEY, JSON.stringify(c));
      }
    });
  }, []);

  useEffect(() => {
    setDismissed(loadJSON<boolean>(DISMISS_KEY, false));
    setManual(loadJSON<Record<string, boolean>>(MANUAL_KEY, {}));
    // Hydrate from cache so the count is right on the first paint after
    // navigation; refresh in the background.
    const cached = sessionStorage.getItem(COUNTS_CACHE_KEY);

    if (cached) {
      try {
        setCounts(JSON.parse(cached) as Counts);
      } catch {
        // ignore
      }
    }
    refreshCounts();

    const onStorage = (e: StorageEvent) => {
      if (e.key === MANUAL_KEY)
        setManual(loadJSON<Record<string, boolean>>(MANUAL_KEY, {}));
      if (e.key === DISMISS_KEY)
        setDismissed(loadJSON<boolean>(DISMISS_KEY, false));
    };

    window.addEventListener('storage', onStorage);

    return () => window.removeEventListener('storage', onStorage);
  }, [refreshCounts]);

  // The Widget mounts once at the Layout level and persists across SPA
  // navigation, so without this hook the counts would never refresh —
  // creating a class would never flip the course step to "done".
  useEffect(() => {
    router.events.on('routeChangeComplete', refreshCounts);

    return () => {
      router.events.off('routeChangeComplete', refreshCounts);
    };
  }, [router.events, refreshCounts]);

  // Same need but for in-place mutations that don't change the route.
  // Anything that should re-tick a step (createAutomation,
  // createSection, inviteUser, publishCourse) can call
  // signalCountsChanged() and we'll refresh without coupling to it.
  useEffect(() => {
    window.addEventListener(COUNTS_CHANGED_EVENT, refreshCounts);

    return () => {
      window.removeEventListener(COUNTS_CHANGED_EVENT, refreshCounts);
    };
  }, [refreshCounts]);

  // Reflect markStepSeen() writes immediately on the same tab. (The
  // native `storage` event listener already in place only fires for
  // OTHER tabs.)
  useEffect(() => {
    const onSeen = () =>
      setManual(loadJSON<Record<string, boolean>>(MANUAL_KEY, {}));

    window.addEventListener(STEP_SEEN_EVENT, onSeen);

    return () => window.removeEventListener(STEP_SEEN_EVENT, onSeen);
  }, []);

  // Hide on the getting-started page itself — no point duplicating the checklist.
  if (router.pathname === '/admin/getting-started') return null;
  if (dismissed) return null;

  const fromCounts: Partial<Record<StepKey, boolean>> = counts
    ? {
        automations: counts.automations > 0,

        // Paid customers have a Stripe `customer` id on their church.
        billing: counts.hasStripeCustomer,

        // Logo is the strongest single signal that branding has been
        // customized — guests see it at the top of every class.
        branding: !!counts.churchLogo,
        course: counts.courses > 0,
        ministries: counts.ministries > 0,
        pco: counts.hasPco,
        // The church profile counts as set once name and URL slug are both
        // populated — both are required to share a class link.
        profile: !!counts.churchName && !!counts.churchUrl,
        // "Publish" needs both: at least one course has no pending changes
        // (Historical row exists for the current state) AND the church is
        // actually paying. We require both because Historical rows have
        // historically been created for some unpaid churches too — without
        // the billing gate this signal lights up incorrectly for them.
        publish: counts.anyPublished && counts.hasStripeCustomer,
        sections: counts.sectionsBeyondDefault,
        team: counts.users > 1,
      }
    : {};
  const isDone = (k: StepKey) => !!signals[k] || !!fromCounts[k] || !!manual[k];
  const completed = steps.filter(s => isDone(s.key)).length;
  const total = steps.length;

  // If everything we can detect is done AND the user hasn't dismissed, still
  // hide — they're effectively done.
  if (completed >= total) return null;

  const nextStep = steps.find(s => !isDone(s.key)) ?? steps[0];

  const dismiss = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    localStorage.setItem(DISMISS_KEY, 'true');
    setDismissed(true);
    posthog?.capture('onboardingWidgetDismissed');
  };

  // When a per-page callout is active (any `?onboarding=<step>` in the URL)
  // it already shows the step's guidance, so the progress widget in the same
  // corner is redundant — on mobile it's a bottom sheet over the widget, and
  // on desktop the two cards just stack. Hide the widget on every breakpoint
  // while a callout is open so the user only ever sees one box.
  const calloutOpen = !!router.query.onboarding;

  return (
    <aside
      aria-label="Getting started"
      css={mq({
        animation: `${slideUp} 0.25s ease`,
        background: theme.colors.white,
        border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.radius.md,
        // Sit above the Help Scout beacon (~60px tall + ~20px margin) so the
        // beacon stays clickable at the bottom-right corner.
        bottom: ['8.5rem', undefined, '9rem'],
        boxShadow: theme.shadows.md,
        display: calloutOpen ? 'none' : 'block',
        maxWidth: ['calc(100vw - 2rem)', undefined, '32rem'],
        overflow: 'hidden',
        position: 'fixed',
        right: ['1rem', undefined, '1.6rem'],
        width: ['calc(100vw - 2rem)', undefined, '32rem'],
        zIndex: 5,
      })}
      role="region"
    >
      <button
        aria-label={
          expanded ? 'Collapse getting started' : 'Expand getting started'
        }
        css={css`
          display: grid;
          grid-template-columns: auto 1fr auto;
          column-gap: 1rem;
          align-items: center;
          width: 100%;
          padding: 0.8rem 1rem 0.8rem 1.2rem;
          color: ${theme.colors.black};
          text-align: left;
          background: ${theme.colors.white};
          border: 0;
          cursor: pointer;
          &:hover {
            background: ${theme.colors.whiteHover};
          }
        `}
        type="button"
        onClick={() => {
          setExpanded(x => !x);
          posthog?.capture('onboardingWidgetToggled', {
            expanded: !expanded,
          });
        }}
      >
        <ProgressRing completed={completed} theme={theme} total={total} />
        <span
          css={{
            minWidth: 0,
          }}
        >
          <span
            css={{
              color: theme.colors.grayMed,
              display: 'block',
              fontSize: '1rem',
              fontWeight: 600,
              letterSpacing: '0.5px',
              lineHeight: 1,
              textTransform: 'uppercase',
            }}
          >
            Up next
          </span>
          <span
            css={{
              color: theme.colors.black,
              display: 'block',
              fontSize: '1.3rem',
              fontWeight: 500,
              lineHeight: 1.2,
              marginTop: '0.3rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {nextStep.title}
          </span>
        </span>
        <span
          css={{
            alignItems: 'center',
            display: 'inline-flex',
            gap: '0.2rem',
          }}
        >
          <span
            css={{
              alignItems: 'center',
              borderRadius: '50%',
              color: theme.colors.grayMed,
              display: 'inline-flex',
              height: '2.4rem',
              justifyContent: 'center',
              width: '2.4rem',
            }}
          >
            <Icon name={expanded ? 'ARROWS_Down' : 'ARROWS_Up'} size="12" />
          </span>
          <span
            aria-label="Dismiss"
            css={{
              '&:hover': { background: theme.colors.background },
              alignItems: 'center',
              borderRadius: '50%',
              color: theme.colors.grayMed,
              cursor: 'pointer',
              display: 'inline-flex',
              height: '2.4rem',
              justifyContent: 'center',
              width: '2.4rem',
            }}
            role="button"
            tabIndex={0}
            onClick={dismiss}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                dismiss();
              }
            }}
          >
            <Icon name="ACTIONS_Close" size="10" />
          </span>
        </span>
      </button>

      {expanded && (
        <div css={{ borderTop: `1px solid ${theme.colors.border}` }}>
          <div
            css={{
              background: theme.colors.border,
              height: '0.4rem',
              overflow: 'hidden',
            }}
          >
            <div
              css={{
                background: theme.colors.clickable,
                height: '100%',
                transition: 'width 0.3s',
                width: `${Math.round((completed / total) * 100)}%`,
              }}
            />
          </div>
          <div
            css={css`
              max-height: 28rem;
              overflow-y: auto;
              padding: 0.4rem 0.6rem 0.6rem;
            `}
          >
            {(['build', 'setup', 'launch'] as StepGroup[]).map(g => {
              const groupSteps = steps.filter(s => s.group === g);
              const groupDone = groupSteps.filter(s => isDone(s.key)).length;
              const groupTotal = groupSteps.length;

              return (
                <div key={g} css={{ marginTop: '0.6rem' }}>
                  <div
                    css={{
                      alignItems: 'baseline',
                      color: theme.colors.grayMed,
                      display: 'flex',
                      fontSize: '1.05rem',
                      fontWeight: 600,
                      gap: '0.6rem',
                      justifyContent: 'space-between',
                      letterSpacing: '0.5px',
                      padding: '0.4rem 0.8rem 0.3rem',
                      textTransform: 'uppercase',
                    }}
                  >
                    <span>{groupLabels[g]}</span>
                    <span css={{ fontWeight: 500 }}>
                      {groupDone}/{groupTotal}
                    </span>
                  </div>
                  <ul
                    css={css`
                      display: grid;
                      gap: 0.1rem;
                      margin: 0;
                      padding: 0;
                      list-style: none;
                    `}
                  >
                    {groupSteps.map(s => {
                      const done = isDone(s.key);
                      const href = `${getStepHref(s.key, {
                        churchUrl: counts?.churchUrl,
                        firstCourseId: counts?.firstCourseId,
                        firstCourseUrl: counts?.firstCourseUrl,
                      })}?onboarding=${s.key}`;

                      return (
                        <li key={s.key}>
                          <Link
                            css={css`
                              display: grid;
                              grid-template-columns: auto 1fr;
                              column-gap: 0.8rem;
                              align-items: center;
                              padding: 0.5rem 0.8rem;
                              color: ${theme.colors.black};
                              font-size: 1.3rem;
                              text-decoration: none;
                              border-radius: ${theme.radius.md};
                              &:hover {
                                background: ${theme.colors.whiteHover};
                              }
                            `}
                            href={href}
                            onClick={() => {
                              setExpanded(false);
                              posthog?.capture('onboardingWidgetStepClicked', {
                                key: s.key,
                              });
                            }}
                          >
                            <StepCheck done={done} size="small" />
                            <span
                              css={{
                                opacity: done ? 0.6 : 1,
                                textDecoration: done ? 'line-through' : 'none',
                              }}
                            >
                              {s.title}
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
          <div
            css={{
              borderTop: `1px solid ${theme.colors.border}`,
              padding: '0.8rem 1.2rem',
              textAlign: 'right',
            }}
          >
            <Link
              css={{
                color: theme.colors.clickable,
                fontSize: '1.3rem',
                fontWeight: 500,
                textDecoration: 'none',
              }}
              href="/admin/getting-started"
            >
              Open full walkthrough →
            </Link>
          </div>
        </div>
      )}
    </aside>
  );
};

export default Widget;
